import type { FeatureCollection } from "geojson";
import { Geo, type GeoInput, type GeoInputAsync } from "@rogoag/geo";

export async function toGeoJSONFile(data: GeoInputAsync, filename: string, filePath?: string): Promise<File> {
	const fc = data instanceof Blob ? await Geo.toGeoJSON(data, filePath) : Geo.toGeoJSON(data as GeoInput, filePath);
	return new File([JSON.stringify(fc)], filename, { type: "application/geo+json" });
}

export async function toKMLFile(data: GeoInputAsync, filename: string, filePath?: string): Promise<File> {
	const kml = data instanceof Blob ? await Geo.toKML(data, filePath) : Geo.toKML(data as GeoInput, filePath);
	return new File([kml], filename, { type: "application/vnd.google-earth.kml+xml" });
}

export async function toKMZFile(data: GeoInputAsync, filename: string, filePath?: string): Promise<File> {
	const buf = data instanceof Blob ? await Geo.toKMZ(data, filePath) : Geo.toKMZ(data as GeoInput, filePath);
	// @rogoag/geo declares a bare `Uint8Array` (TS 6 defaults the type param to `ArrayBufferLike`),
	// but `File`/`Blob` now require `ArrayBufferView<ArrayBuffer>`. The bytes are always backed by
	// a real ArrayBuffer (never a SharedArrayBuffer) — assert the concrete type, no copy needed.
	return new File([buf as Uint8Array<ArrayBuffer>], filename, { type: "application/vnd.google-earth.kmz" });
}

export async function toSHPFile(data: GeoInputAsync, filename: string, filePath?: string): Promise<File> {
	const buf = data instanceof Blob ? await Geo.toSHP(data, filePath) : Geo.toSHP(data as GeoInput, filePath);
	return new File([buf as Uint8Array<ArrayBuffer>], filename, { type: "application/zip" });
}

/**
 * A boundary as the two derived fields job prep writes from its shapefile (pre-sampling-processor's
 * `process_boundary`): the full FeatureCollection as a `boundary.json` file for the "Bnd GeoJSON" attachment,
 * and a copy simplified to fit Airtable's long-text limit for "Bnd GeoJSON Text", which Mission Control and the
 * portal's map read first.
 */
export function toBoundaryGeoJSON(fc: FeatureCollection): { file: File; text: string } {
	return {
		file: new File([JSON.stringify(fc)], "boundary.json", { type: "application/json" }),
		text: Geo.simplifyFeatureCollectionToFit(fc),
	};
}
